import { useState } from 'react';
import { IconAction, RowActions, TextSkeleton } from '@dru/ui';
import { Download, FileText } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';

interface DocumentsView {
  state: string;
  stateName: string;
  rules: string;
  items: Array<{ kind: string; label: string; detail: string; available: boolean; reason?: string }>;
}

/**
 * The statutory paper for one case: the Health Register entry and the
 * Certificate of Fitness in the state's prescribed form, a health card,
 * and everything in one PDF. Rendered on demand from the signed decision
 * and the released results; nothing here is a new record.
 */
export function CaseDocuments({ orderId }: { orderId: string }) {
  const docs = useQuery<DocumentsView>(() => api.get(`/health-reports/orders/${orderId}/documents`), [orderId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function download(kind: string) {
    setBusy(kind); setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/health-reports/orders/${orderId}/documents/${kind}/download`);
      window.open(grant.url, '_blank', 'noopener');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  if (docs.loading && !docs.data) return <TextSkeleton lines={3} />;
  if (docs.error) return <p className="text-small text-neutral-500">{docs.error}</p>;
  const v = docs.data!;
  return (
    <div className="space-y-2">
      <p className="text-caption text-neutral-600">{v.rules} — forms as prescribed for {v.stateName}.</p>
      <ul className="divide-y divide-neutral-100 -mx-4">
        {v.items.map((d) => (
          <li key={d.kind} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-small text-neutral-900">
                <FileText className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
                <span>{d.label}</span>
              </div>
              <div className="pl-6 text-caption text-neutral-600">{d.available ? d.detail : d.reason}</div>
            </div>
            <RowActions>
              <IconAction
                label={d.available ? `Download ${d.label} (PDF)` : `${d.label}: ${d.reason ?? 'not available yet'}`}
                icon={Download}
                disabled={!d.available}
                loading={busy === d.kind}
                onClick={() => void download(d.kind)}
              />
            </RowActions>
          </li>
        ))}
      </ul>
      {error && <p role="alert" className="text-caption text-danger-600">{error}</p>}
    </div>
  );
}
